import React, { useMemo } from "react";

/**
 * Table of Contents — auto-generated from H2/H3 headings in a Payload
 * Lexical rich-text tree. Renders an ordered anchor list. The section
 * *label* (e.g. "In this article") is owned by the CALLER (see
 * `<TocCollapsible>` in BlogArticle.jsx) so it never duplicates.
 *
 * The parent article body sets matching `id` attributes on its H2/H3
 * elements via LexicalRenderer's `slugifyHeading()` — kept in lockstep
 * with `slugifyHeading` exported below.
 */
export default function TableOfContents({ content, className = "" }) {
  const headings = useMemo(() => extractHeadings(content), [content]);

  if (headings.length < 3) return null;  // TOC only for substantial articles

  return (
    <nav
      className={`toc-list ${className}`}
      aria-label="Table of contents"
      data-testid="table-of-contents"
    >
      <ol className="space-y-1.5 text-[13px] leading-snug">
        {headings.map((h, i) => (
          <li
            key={i}
            className={h.level === 3 ? "ml-4" : ""}
            data-testid={`toc-item-${i}`}
          >
            <a
              href={`#${h.id}`}
              className="text-neutral-700 hover:text-neutral-950 transition-colors"
            >
              {h.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

/** Slugify a heading string identically to LexicalRenderer's anchors. */
export function slugifyHeading(text) {
  return String(text || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

/** Walk the Lexical tree and extract H2/H3 text + generated id. */
function extractHeadings(content) {
  if (!content || typeof content !== "object") return [];
  const root = content.root || content;
  const out = [];
  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (node.type === "heading") {
      const tag = String(node.tag || "").toLowerCase();
      if (tag === "h2" || tag === "h3") {
        const text = extractText(node).trim();
        if (text) {
          out.push({ text, level: tag === "h2" ? 2 : 3, id: slugifyHeading(text) });
        }
      }
      return;
    }
    for (const c of (node.children || [])) walk(c);
  };
  walk(root);
  return out;
}

function extractText(node) {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (node.type === "text" && typeof node.text === "string") return node.text;
  return (node.children || []).map(extractText).join("");
}
